/**
 * Augmented Live streaming codec — Phase 2 (ENG-6255).
 * Design: docs/design/here-now-s3-realtime.md ("Overcoming the 256 KB broadcast cap")
 *
 * The private draft preview streams an artifact *as it is written*, at
 * file-save granularity, over a Supabase Realtime broadcast channel whose
 * payload is capped at ~256 KB. Sending the whole document on every save would
 * blow that cap for any non-trivial artifact, so — exactly like a video codec —
 * we send *deltas, not documents*:
 *
 *   1. **Keyframes (I-frames)** — periodic full snapshots. The resync anchor: a
 *      viewer that joins mid-stream or detects a gap waits for the next one
 *      instead of corrupting state. Supabase broadcast is best-effort,
 *      unordered and lossy, so one dropped patch would otherwise break every
 *      later patch.
 *   2. **Patch frames (P-frames)** — the common case. During generation the
 *      agent rewrites one file, so consecutive saves differ by a little. Each
 *      patch is a strict (exact, not fuzzy) prefix/suffix diff against the
 *      *previous frame* the decoder holds — a 200-char edit in a 500 KB doc is
 *      a sub-1 KB frame.
 *   3. **Chunking** — any frame whose serialized size exceeds the byte budget
 *      (headroom under the 256 KB cap) is fragmented and reassembled atomically
 *      by the decoder, which never renders a half-document.
 *
 * Every frame carries `seq`; patches additionally carry `baseSeq`. A patch
 * applies only if `baseSeq === lastApplied`; on a gap the decoder stops
 * applying diffs and waits for the next keyframe. Worst case a desynced viewer
 * shows a slightly-stale preview for one keyframe interval — never corruption.
 *
 * This module is **pure and isomorphic** (no I/O, no Node/DOM globals beyond
 * `TextEncoder`, which exists in Node ≥ 11 and every browser). The manager
 * (encode) and the webapp console preview (decode) share it verbatim so the
 * wire format can never drift between the two sides.
 *
 * Locked parameters (ADR, ENG-6234): keyframe every 10 frames OR 5 s,
 * ~180 KB chunk byte-budget (headroom under the 256 KB cap).
 */

/** Wire-format version. Bump only on an incompatible frame-shape change. */
export const CODEC_VERSION = 1;

/** Keyframe cadence: force a full snapshot at least every N frames. */
export const DEFAULT_KEYFRAME_INTERVAL = 10;
/** Keyframe cadence: force a full snapshot at least every N milliseconds. */
export const DEFAULT_KEYFRAME_INTERVAL_MS = 5_000;
/**
 * Per-frame serialized byte budget. Frames larger than this are chunked. Set
 * well under Supabase's ~256 KB broadcast cap to leave headroom for the
 * transport envelope the broadcast API wraps around our payload.
 */
export const DEFAULT_CHUNK_BUDGET_BYTES = 180 * 1024;

/**
 * One diff operation, applied left-to-right against the base string with an
 * implicit cursor (an OT/`diff_toDelta`-style stream):
 *   - `retain` — copy N code units from base at the cursor, advance cursor.
 *   - `delete` — skip N code units of base (drop them), advance cursor.
 *   - `insert` — append the literal string (cursor unchanged).
 * The encoder guarantees `sum(retain) + sum(delete) === base.length`, so the
 * decoder consumes the whole base exactly — a mismatch means corruption.
 */
export type PatchOp =
  | { retain: number }
  | { delete: number }
  | { insert: string };

/** A periodic full snapshot — the resync anchor. */
export interface KeyFrame {
  v: typeof CODEC_VERSION;
  type: 'key';
  seq: number;
  content: string;
}

/** A delta against the frame `baseSeq` (the previous frame the decoder holds). */
export interface PatchFrame {
  v: typeof CODEC_VERSION;
  type: 'patch';
  seq: number;
  baseSeq: number;
  ops: PatchOp[];
}

/**
 * One fragment of an over-budget key or patch frame. All fragments for a given
 * `seq` share `parts`; concatenating their `data` in `part` order reconstructs
 * the underlying frame's payload (the key's `content`, or `JSON.stringify(ops)`
 * for a patch).
 */
export interface ChunkFrame {
  v: typeof CODEC_VERSION;
  type: 'chunk';
  seq: number;
  /** Which kind of frame this reassembles into. */
  kind: 'key' | 'patch';
  /** Present only when `kind === 'patch'` — the reconstructed patch's baseSeq. */
  baseSeq?: number;
  /** 0-based fragment index. */
  part: number;
  /** Total fragment count for this `seq`. */
  parts: number;
  data: string;
}

export type StreamFrame = KeyFrame | PatchFrame | ChunkFrame;

const textEncoder = new TextEncoder();

/** UTF-8 byte length of a string (matches the bytes the broadcast transports). */
export function byteLength(value: string): number {
  return textEncoder.encode(value).length;
}

/** Serialized byte size of a frame, as it will travel on the wire. */
export function frameByteLength(frame: StreamFrame): number {
  return byteLength(JSON.stringify(frame));
}

// ---------------------------------------------------------------------------
// Diff — strict prefix/suffix splice.
//
// The decoder holds the *exact* previous string, so we never need fuzzy
// matching: the common prefix and common suffix are unchanged, everything
// between them is replaced. This is optimal for the dominant single-region
// edit, dependency-free, and degrades to a larger insert (never to incorrect
// output) for scattered edits — where the periodic keyframe is the safety net.
// Indices are UTF-16 code units; because the same boundaries are sliced out of
// `base` and copied verbatim from `next`, reconstruction is byte-exact even if
// a boundary falls between a surrogate pair.
// ---------------------------------------------------------------------------

/** Compute the minimal-region patch turning `base` into `next`. */
export function diffStrings(base: string, next: string): PatchOp[] {
  const baseLen = base.length;
  const nextLen = next.length;

  let prefix = 0;
  const maxPrefix = Math.min(baseLen, nextLen);
  while (prefix < maxPrefix && base.charCodeAt(prefix) === next.charCodeAt(prefix)) {
    prefix++;
  }

  let suffix = 0;
  const maxSuffix = Math.min(baseLen, nextLen) - prefix;
  while (
    suffix < maxSuffix &&
    base.charCodeAt(baseLen - 1 - suffix) === next.charCodeAt(nextLen - 1 - suffix)
  ) {
    suffix++;
  }

  const deleteCount = baseLen - prefix - suffix;
  const inserted = next.slice(prefix, nextLen - suffix);

  const ops: PatchOp[] = [];
  if (prefix > 0) ops.push({ retain: prefix });
  if (deleteCount > 0) ops.push({ delete: deleteCount });
  if (inserted.length > 0) ops.push({ insert: inserted });
  if (suffix > 0) ops.push({ retain: suffix });
  // Identical strings yield a single full-length retain so apply round-trips.
  if (ops.length === 0) ops.push({ retain: baseLen });
  return ops;
}

/**
 * Apply a patch to its base string. Throws if the ops don't consume the base
 * exactly — the caller treats that as a desync and waits for the next keyframe.
 */
export function applyOps(base: string, ops: PatchOp[]): string {
  let cursor = 0;
  let out = '';
  for (const op of ops) {
    if ('retain' in op) {
      const end = cursor + op.retain;
      if (op.retain < 0 || end > base.length) {
        throw new Error('codec: retain out of bounds');
      }
      out += base.slice(cursor, end);
      cursor = end;
    } else if ('delete' in op) {
      const end = cursor + op.delete;
      if (op.delete < 0 || end > base.length) {
        throw new Error('codec: delete out of bounds');
      }
      cursor = end;
    } else {
      out += op.insert;
    }
  }
  if (cursor !== base.length) {
    throw new Error('codec: ops did not consume base exactly');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Chunking
// ---------------------------------------------------------------------------

/**
 * Split `payload` into substrings whose UTF-8 byte length each fits within
 * `budgetBytes`. Splits on code-point boundaries (never inside a surrogate
 * pair), so reassembly via plain concatenation is lossless.
 */
function splitByBytes(payload: string, budgetBytes: number): string[] {
  const parts: string[] = [];
  let current = '';
  let currentBytes = 0;
  for (const cp of payload) {
    const cpBytes = byteLength(cp);
    if (currentBytes + cpBytes > budgetBytes && current.length > 0) {
      parts.push(current);
      current = '';
      currentBytes = 0;
    }
    current += cp;
    currentBytes += cpBytes;
  }
  if (current.length > 0 || parts.length === 0) parts.push(current);
  return parts;
}

/**
 * Fragment an over-budget frame into chunk frames. Returns the frame unchanged
 * (as a single-element array) when it already fits.
 */
export function chunkFrame(
  frame: KeyFrame | PatchFrame,
  budgetBytes: number = DEFAULT_CHUNK_BUDGET_BYTES,
): StreamFrame[] {
  if (frameByteLength(frame) <= budgetBytes) return [frame];

  const payload = frame.type === 'key' ? frame.content : JSON.stringify(frame.ops);
  // Reserve headroom for the chunk envelope (seq/parts/kind/baseSeq fields).
  const dataBudget = Math.max(1, budgetBytes - 512);
  const pieces = splitByBytes(payload, dataBudget);

  return pieces.map((data, index) => {
    const chunk: ChunkFrame = {
      v: CODEC_VERSION,
      type: 'chunk',
      seq: frame.seq,
      kind: frame.type,
      part: index,
      parts: pieces.length,
      data,
    };
    if (frame.type === 'patch') chunk.baseSeq = frame.baseSeq;
    return chunk;
  });
}

// ---------------------------------------------------------------------------
// Encoder (manager side)
// ---------------------------------------------------------------------------

export interface StreamEncoderOptions {
  keyframeInterval?: number;
  keyframeIntervalMs?: number;
  chunkBudgetBytes?: number;
  /** Injectable clock (testability). Defaults to `Date.now`. */
  now?: () => number;
}

/**
 * Stateful per-draft encoder. Call {@link StreamEncoder.encode} once per file
 * save; it returns the frame(s) to broadcast (more than one only when a frame
 * had to be chunked). The first save, every Nth save, and any save older than
 * the time interval emit a keyframe; the rest emit patches against the prior
 * frame.
 */
export class StreamEncoder {
  private readonly keyframeInterval: number;
  private readonly keyframeIntervalMs: number;
  private readonly chunkBudgetBytes: number;
  private readonly now: () => number;

  private seq = 0;
  private prevContent: string | null = null;
  private prevSeq = 0;
  private framesSinceKey = 0;
  private lastKeyAt = 0;

  constructor(options: StreamEncoderOptions = {}) {
    this.keyframeInterval = options.keyframeInterval ?? DEFAULT_KEYFRAME_INTERVAL;
    this.keyframeIntervalMs = options.keyframeIntervalMs ?? DEFAULT_KEYFRAME_INTERVAL_MS;
    this.chunkBudgetBytes = options.chunkBudgetBytes ?? DEFAULT_CHUNK_BUDGET_BYTES;
    this.now = options.now ?? (() => Date.now());
  }

  /** Encode one snapshot of the artifact into wire frames. */
  encode(content: string): StreamFrame[] {
    const seq = ++this.seq;
    const now = this.now();

    const mustKeyframe =
      this.prevContent === null ||
      this.framesSinceKey >= this.keyframeInterval ||
      now - this.lastKeyAt >= this.keyframeIntervalMs;

    let frame: KeyFrame | PatchFrame;
    if (mustKeyframe) {
      frame = { v: CODEC_VERSION, type: 'key', seq, content };
      this.framesSinceKey = 0;
      this.lastKeyAt = now;
    } else {
      frame = {
        v: CODEC_VERSION,
        type: 'patch',
        seq,
        baseSeq: this.prevSeq,
        ops: diffStrings(this.prevContent as string, content),
      };
      this.framesSinceKey++;
    }

    this.prevContent = content;
    this.prevSeq = seq;
    return chunkFrame(frame, this.chunkBudgetBytes);
  }

  /** Reset to the initial state (e.g. a new draft on the same encoder). */
  reset(): void {
    this.seq = 0;
    this.prevContent = null;
    this.prevSeq = 0;
    this.framesSinceKey = 0;
    this.lastKeyAt = 0;
  }
}

// ---------------------------------------------------------------------------
// Decoder (console-preview side)
// ---------------------------------------------------------------------------

export interface DecodeResult {
  /** The current full document, or null before the first keyframe/seed. */
  content: string | null;
  /** Whether this frame advanced the document. */
  applied: boolean;
  /**
   * True when the decoder is holding stale content and waiting for the next
   * keyframe to resync (a gap, an out-of-order patch, or a seed with no
   * matching baseSeq yet). The UI should show "reconnecting", not corruption.
   */
  desynced: boolean;
}

/**
 * Stateful decoder. Feed every received frame to {@link StreamDecoder.apply};
 * render {@link DecodeResult.content} into the sandboxed preview iframe. A late
 * joiner can {@link StreamDecoder.seed} from the last-published CloudFront
 * version so the preview shows *something* immediately, then snaps to live on
 * the next keyframe.
 */
export class StreamDecoder {
  private currentContent: string | null = null;
  private lastAppliedSeq: number | null = null;
  private desyncedState = false;
  /** In-flight chunk reassembly buffers, keyed by frame seq. */
  private readonly chunks = new Map<
    number,
    { kind: 'key' | 'patch'; baseSeq?: number; parts: number; received: Map<number, string> }
  >();

  get content(): string | null {
    return this.currentContent;
  }

  get desynced(): boolean {
    return this.desyncedState;
  }

  /**
   * Seed the preview from a known-good full document (e.g. the last published
   * version) before any live frame arrives. The decoder still treats the next
   * patch as a gap (no matching seq) and waits for a keyframe — this only gives
   * the viewer something to look at meanwhile.
   */
  seed(content: string): void {
    this.currentContent = content;
    this.lastAppliedSeq = null;
    this.desyncedState = true;
    this.chunks.clear();
  }

  /** Apply one received frame. */
  apply(frame: StreamFrame): DecodeResult {
    if (frame.type === 'chunk') return this.applyChunk(frame);
    if (frame.type === 'key') return this.applyKey(frame);
    return this.applyPatch(frame);
  }

  /** Reset to the initial empty state. */
  reset(): void {
    this.currentContent = null;
    this.lastAppliedSeq = null;
    this.desyncedState = false;
    this.chunks.clear();
  }

  private applyKey(frame: KeyFrame): DecodeResult {
    this.currentContent = frame.content;
    this.lastAppliedSeq = frame.seq;
    this.desyncedState = false;
    this.prune(frame.seq);
    return this.result(true);
  }

  private applyPatch(frame: PatchFrame): DecodeResult {
    // No base yet, or a gap: refuse to apply and wait for the next keyframe.
    if (this.currentContent === null || this.lastAppliedSeq !== frame.baseSeq) {
      this.desyncedState = true;
      return this.result(false);
    }
    try {
      this.currentContent = applyOps(this.currentContent, frame.ops);
    } catch {
      // Corrupt/unexpected ops — drop to desynced, await a keyframe.
      this.desyncedState = true;
      return this.result(false);
    }
    this.lastAppliedSeq = frame.seq;
    this.desyncedState = false;
    this.prune(frame.seq);
    return this.result(true);
  }

  private applyChunk(frame: ChunkFrame): DecodeResult {
    let entry = this.chunks.get(frame.seq);
    if (!entry) {
      entry = {
        kind: frame.kind,
        baseSeq: frame.baseSeq,
        parts: frame.parts,
        received: new Map(),
      };
      this.chunks.set(frame.seq, entry);
    }
    entry.received.set(frame.part, frame.data);
    if (entry.received.size < entry.parts) {
      // Still assembling — nothing rendered yet.
      return this.result(false);
    }

    let payload = '';
    for (let i = 0; i < entry.parts; i++) {
      const piece = entry.received.get(i);
      if (piece === undefined) {
        // A part is missing despite the count matching — give up on this seq.
        this.chunks.delete(frame.seq);
        this.desyncedState = true;
        return this.result(false);
      }
      payload += piece;
    }
    this.chunks.delete(frame.seq);

    if (entry.kind === 'key') {
      return this.applyKey({ v: CODEC_VERSION, type: 'key', seq: frame.seq, content: payload });
    }
    let ops: PatchOp[];
    try {
      ops = JSON.parse(payload) as PatchOp[];
    } catch {
      this.desyncedState = true;
      return this.result(false);
    }
    return this.applyPatch({
      v: CODEC_VERSION,
      type: 'patch',
      seq: frame.seq,
      baseSeq: entry.baseSeq ?? -1,
      ops,
    });
  }

  /** Drop reassembly buffers for frames at or before the applied seq. */
  private prune(throughSeq: number): void {
    for (const seq of this.chunks.keys()) {
      if (seq <= throughSeq) this.chunks.delete(seq);
    }
  }

  private result(applied: boolean): DecodeResult {
    return { content: this.currentContent, applied, desynced: this.desyncedState };
  }
}

/**
 * Structural validation of an untrusted wire frame before it is broadcast (the
 * stream route, slice C) or applied (the console preview, slice E). A cheap
 * shape check, not a deep audit — enough to reject garbage so the private
 * channel only ever carries well-formed frames. The decoder remains defensive
 * about out-of-order / corrupt patches on top of this.
 */
export function isStreamFrame(value: unknown): value is StreamFrame {
  if (!value || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  if (f['v'] !== CODEC_VERSION) return false;
  if (typeof f['seq'] !== 'number') return false;
  switch (f['type']) {
    case 'key':
      return typeof f['content'] === 'string';
    case 'patch':
      return typeof f['baseSeq'] === 'number' && Array.isArray(f['ops']);
    case 'chunk':
      return (
        (f['kind'] === 'key' || f['kind'] === 'patch') &&
        typeof f['part'] === 'number' &&
        typeof f['parts'] === 'number' &&
        typeof f['data'] === 'string' &&
        // Mirror the encoder: a patch chunk carries the reconstructed patch's
        // baseSeq, a key chunk never does.
        (f['kind'] === 'key' ? f['baseSeq'] === undefined : typeof f['baseSeq'] === 'number')
      );
    default:
      return false;
  }
}

/**
 * Realtime channel name for a draft stream. Mirrors `publicChannelName(slug)`
 * (`artifact:{slug}`) but for the **private, team-authenticated** draft channel
 * keyed on the artifact's internal id. Broadcast with `private: true`; only
 * authenticated team members may subscribe (enforced by `realtime.messages`
 * RLS — slice A / ENG-6254).
 */
export function draftChannelName(id: string): string {
  return `artifact-draft:${id}`;
}
